// Lofted bodies: a car panel is a list of cross-sections along its length
// (z, forward is +z), each a superellipse with its own width, height, centre
// and roundness. The loft interpolates them smoothly (Catmull-Rom on every
// parameter) and closes both ends. UVs wrap around the section (v) and run
// along the length (u), so a livery can be painted on a flat canvas:
//   v = 0 bottom, 0.25 right side (+x), 0.5 top, 0.75 left side, 1 bottom
//   u = 0 at the rear of the panel, 1 at the front
import * as THREE from 'three'

export type Section = {
  z: number
  w: number // half width
  h: number // half height
  cy: number // centre height
  cx?: number // centre offset (side panels)
  top?: number // superellipse exponent for the top half (2 = ellipse, 4+ = boxier)
  bottom?: number // ... for the bottom half
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

function catmull(p0: number, p1: number, p2: number, p3: number, t: number) {
  const t2 = t * t
  const t3 = t2 * t
  return 0.5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3)
}

function full(s: Section): Required<Section> {
  return { cx: 0, top: 2.4, bottom: 3.2, ...s }
}

// Sample the sections at `steps` points along the loft.
export function sampleSections(sections: Section[], steps: number): Required<Section>[] {
  const list = sections.map(full)
  const out: Required<Section>[] = []
  const keys = ['z', 'w', 'h', 'cy', 'cx', 'top', 'bottom'] as const
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * (list.length - 1)
    const k = Math.min(Math.floor(t), list.length - 2)
    const f = t - k
    const p0 = list[Math.max(0, k - 1)]
    const p1 = list[k]
    const p2 = list[k + 1]
    const p3 = list[Math.min(list.length - 1, k + 2)]
    const s = {} as Required<Section>
    for (const key of keys) s[key] = key === 'top' || key === 'bottom' ? lerp(p1[key], p2[key], f) : catmull(p0[key], p1[key], p2[key], p3[key], f)
    s.w = Math.max(0.002, s.w)
    s.h = Math.max(0.002, s.h)
    out.push(s)
  }
  return out
}

export function sectionPoint(s: Required<Section>, v: number): [number, number] {
  const a = -Math.PI / 2 + v * Math.PI * 2
  const c = Math.cos(a)
  const sn = Math.sin(a)
  const n = sn >= 0 ? s.top : s.bottom
  const x = s.cx + s.w * Math.sign(c) * Math.abs(c) ** (2 / n)
  const y = s.cy + s.h * Math.sign(sn) * Math.abs(sn) ** (2 / n)
  return [x, y]
}

export function loft(sections: Section[], { steps = 64, around = 48, mirrorX = false } = {}): THREE.BufferGeometry {
  const rows = sampleSections(sections, steps)
  const zMin = Math.min(...rows.map((r) => r.z))
  const zMax = Math.max(...rows.map((r) => r.z))
  const positions: number[] = []
  const uvs: number[] = []
  const indices: number[] = []
  const flip = mirrorX ? -1 : 1

  rows.forEach((row) => {
    for (let j = 0; j <= around; j++) {
      const v = j / around
      const [x, y] = sectionPoint(row, v)
      positions.push(x * flip, y, row.z)
      uvs.push((row.z - zMin) / (zMax - zMin || 1), mirrorX ? 1 - v : v)
    }
  })
  const width = around + 1
  for (let i = 0; i < rows.length - 1; i++) {
    for (let j = 0; j < around; j++) {
      const a = i * width + j
      const b = a + width
      // Winding so normals point outward (sections run rear to front).
      if (mirrorX) indices.push(a, b, a + 1, a + 1, b, b + 1)
      else indices.push(a, a + 1, b, a + 1, b + 1, b)
    }
  }
  // Caps: a fan to each end's centre.
  for (const [rowIndex, outward] of [[0, -1], [rows.length - 1, 1]] as const) {
    const row = rows[rowIndex]
    const centre = positions.length / 3
    positions.push(row.cx * flip, row.cy, row.z)
    uvs.push(rowIndex === 0 ? 0 : 1, 0.5)
    for (let j = 0; j < around; j++) {
      const a = rowIndex * width + j
      const forward = (outward === 1) !== mirrorX
      if (forward) indices.push(centre, a, a + 1)
      else indices.push(centre, a + 1, a)
    }
  }
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
  geometry.setIndex(indices)
  geometry.computeVertexNormals()
  return geometry
}

// A thin aerofoil (wing element) spanning x from -span/2 to span/2, chord along
// z, with a cambered profile; optional sweep up at the tips.
export function wingElement(span: number, chord: number, thickness: number, { camber = 0.06, tipLift = 0, segments = 24 } = {}): THREE.BufferGeometry {
  const shape = new THREE.Shape()
  const pts: [number, number][] = []
  const n = 16
  for (let i = 0; i <= n; i++) {
    const t = i / n
    const z = chord / 2 - t * chord
    const yc = camber * chord * 4 * t * (1 - t)
    const half = thickness * 0.5 * Math.sin(Math.PI * Math.min(1, t * 1.15 + 0.02)) ** 0.6
    pts.push([z, yc + half])
  }
  for (let i = n; i >= 0; i--) {
    const t = i / n
    const z = chord / 2 - t * chord
    const yc = camber * chord * 4 * t * (1 - t)
    const half = thickness * 0.5 * Math.sin(Math.PI * Math.min(1, t * 1.15 + 0.02)) ** 0.6
    pts.push([z, yc - half * 0.6])
  }
  shape.moveTo(pts[0][0], pts[0][1])
  pts.slice(1).forEach(([z, y]) => shape.lineTo(z, y))
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: span, bevelEnabled: false, steps: segments })
  geometry.translate(0, 0, -span / 2)
  geometry.rotateY(Math.PI / 2)
  if (tipLift) {
    const pos = geometry.attributes.position as THREE.BufferAttribute
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i)
      const r = Math.abs(x) / (span / 2)
      pos.setY(i, pos.getY(i) + tipLift * r ** 3)
    }
  }
  geometry.computeVertexNormals()
  return geometry
}

// A flat panel from a 2D outline (endplates, fins), extruded `thickness`
// across x.
export function plate(outline: [number, number][], thickness: number): THREE.BufferGeometry {
  const shape = new THREE.Shape()
  shape.moveTo(outline[0][0], outline[0][1])
  outline.slice(1).forEach(([z, y]) => shape.lineTo(z, y))
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: true, bevelThickness: thickness * 0.3, bevelSize: thickness * 0.3, bevelSegments: 2 })
  geometry.translate(0, 0, -thickness / 2)
  geometry.rotateY(-Math.PI / 2)
  geometry.computeVertexNormals()
  return geometry
}

// A tube between points (halo, suspension arms, wing pylons).
export function tube(points: THREE.Vector3[], radius: number, segments = 24): THREE.BufferGeometry {
  const curve = points.length === 2 ? new THREE.LineCurve3(points[0], points[1]) : new THREE.CatmullRomCurve3(points)
  return new THREE.TubeGeometry(curve, segments, radius, 10, false)
}
